import { doc, getDoc, onSnapshot, updateDoc, arrayUnion, arrayRemove, Timestamp, Unsubscribe } from 'firebase/firestore';
import { firestore, storageBucketName } from './firebase';

// Global app config: admin allowlist + beta whitelist + subscription pricing
// (settings/settings), and plugin release info (settings/versions).
export type DatabaseSettings = {
    adminEmails: string[];
    // Closed-beta allowlist: the addresses allowed past the auth gate while
    // whitelistEnabled is on. Stored lowercased.
    whitelistedEmails: string[];
    // Master switch for the whitelist. Off means everyone passes, so the beta
    // can be opened up without a redeploy.
    whitelistEnabled: boolean;
    // Monthly all-access subscription price, formatted like a pack price (e.g.
    // "7.99", no currency symbol). null if unset.
    subscriptionPrice: string | null;
    // Stripe price ID for the monthly all-access subscription. null if unset —
    // checkout is disabled until an admin sets it, so it never drifts from
    // subscriptionPrice above (the displayed price).
    subscriptionPriceId: string | null;
    // Yearly all-access subscription price: the full annual charge (e.g.
    // "95.88"), not the per-month figure — see yearlyMonthlyPrice(). null if unset.
    yearlySubscriptionPrice: string | null;
    // Stripe price ID for the yearly all-access subscription. null if unset —
    // the yearly option can't be checked out until an admin sets it.
    yearlySubscriptionPriceId: string | null;
};

// Whether an account may use the app at all, and whether it may administer it.
// Both answers come from settings/settings, so they're resolved together.
export type AccessStatus = {
    isAdmin: boolean;
    isWhitelisted: boolean;
};

export type DatabaseVersions = {
    currentVersion: string | null;
    releaseDates: Record<string, string>; // version → ISO release timestamp
};

// Must match CMakeLists.txt's PRODUCT_NAME, which CI bakes into the installer
// artifact name (see .github/workflows/build_and_test.yml, ARTIFACT_NAME).
const PRODUCT_NAME = 'Organic Electronics';

// Field name constants (kept private to this file)
class FieldPaths {
    static readonly ADMIN_EMAILS = "admin_emails";
    static readonly WHITELISTED_EMAILS = "whitelistedEmails";
    static readonly WHITELIST_ENABLED = "whitelistEnabled";
    static readonly SUBSCRIPTION_PRICE = "subscriptionPrice";
    static readonly SUBSCRIPTION_PRICE_ID = "subscriptionPriceId";
    static readonly YEARLY_SUBSCRIPTION_PRICE = "yearlySubscriptionPrice";
    static readonly YEARLY_SUBSCRIPTION_PRICE_ID = "yearlySubscriptionPriceId";
    static readonly CURRENT_VERSION = "currentVersion";
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export class DatabaseSettingsModel {
    static readonly COLLECTION_NAME = "settings";
    static readonly SETTINGS_DOC_ID = "settings";
    static readonly VERSIONS_DOC_ID = "versions";

    static fromFirestore(data: any): DatabaseSettings {
        return {
            adminEmails: Array.isArray(data?.[FieldPaths.ADMIN_EMAILS]) ? data[FieldPaths.ADMIN_EMAILS] : [],
            whitelistedEmails: Array.isArray(data?.[FieldPaths.WHITELISTED_EMAILS]) ? data[FieldPaths.WHITELISTED_EMAILS] : [],
            whitelistEnabled: data?.[FieldPaths.WHITELIST_ENABLED] === true,
            subscriptionPrice: typeof data?.[FieldPaths.SUBSCRIPTION_PRICE] === 'string' ? data[FieldPaths.SUBSCRIPTION_PRICE] : null,
            subscriptionPriceId: typeof data?.[FieldPaths.SUBSCRIPTION_PRICE_ID] === 'string' ? data[FieldPaths.SUBSCRIPTION_PRICE_ID] : null,
            yearlySubscriptionPrice: typeof data?.[FieldPaths.YEARLY_SUBSCRIPTION_PRICE] === 'string' ? data[FieldPaths.YEARLY_SUBSCRIPTION_PRICE] : null,
            yearlySubscriptionPriceId: typeof data?.[FieldPaths.YEARLY_SUBSCRIPTION_PRICE_ID] === 'string' ? data[FieldPaths.YEARLY_SUBSCRIPTION_PRICE_ID] : null,
        };
    }

    /**
     * The yearly plan's price per month (e.g. "95.88" → "7.99"), the figure
     * advertised wherever the subscription is promoted. null if yearly is unset.
     */
    static yearlyMonthlyPrice(settings: DatabaseSettings | null): string | null {
        const yearly = parseFloat(settings?.yearlySubscriptionPrice ?? '');
        return isNaN(yearly) ? null : (Math.round(yearly / 12 * 100) / 100).toFixed(2);
    }

    static versionsFromFirestore(data: any): DatabaseVersions {
        // Every field other than currentVersion is `<version>: <release Timestamp>`.
        const releaseDates: Record<string, string> = {};
        for (const [key, value] of Object.entries(data ?? {})) {
            if (key !== FieldPaths.CURRENT_VERSION && value instanceof Timestamp) {
                releaseDates[key] = value.toDate().toISOString();
            }
        }
        return {
            currentVersion: typeof data?.[FieldPaths.CURRENT_VERSION] === 'string' ? data[FieldPaths.CURRENT_VERSION] : null,
            releaseDates,
        };
    }

    /**
     * Resolve both access answers for an email in a single read of
     * settings/settings: whether it's an admin (admin_emails) and whether it may
     * pass the auth gate at all (the whitelist).
     *
     * Admins are never gated — an admin passes on the strength of admin_emails
     * alone and never has to appear in whitelistedEmails, so turning the
     * whitelist on (or editing the list) can't lock an admin out. Deriving both
     * here rather than at each call site is what makes that bypass impossible to
     * forget.
     *
     * On error, fails closed on admin and open on the whitelist: a settings read
     * that didn't land shouldn't lock the whole userbase out of the app.
     */
    static async getAccessStatus(userEmail: string | null): Promise<AccessStatus> {
        if (!userEmail) return { isAdmin: false, isWhitelisted: false };

        const email = normalizeEmail(userEmail);

        try {
            const snap = await getDoc(doc(firestore, this.COLLECTION_NAME, this.SETTINGS_DOC_ID));
            if (!snap.exists()) {
                console.warn('Settings document does not exist');
                return { isAdmin: false, isWhitelisted: true };
            }

            const settings = this.fromFirestore(snap.data());
            const isAdmin = settings.adminEmails.some(e => normalizeEmail(e) === email);

            return {
                isAdmin,
                isWhitelisted: !settings.whitelistEnabled
                    || isAdmin
                    || settings.whitelistedEmails.some(e => normalizeEmail(e) === email),
            };
        } catch (error) {
            console.error('Error checking access status:', error);
            return { isAdmin: false, isWhitelisted: true };
        }
    }

    /**
     * Subscribe to the whole settings document, so admin UI reflects edits (its
     * own and other admins') without a reload. Returns an Unsubscribe — call it
     * on cleanup.
     */
    static subscribeSettings(
        onUpdate: (settings: DatabaseSettings) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        return onSnapshot(
            doc(firestore, this.COLLECTION_NAME, this.SETTINGS_DOC_ID),
            (snap) => onUpdate(this.fromFirestore(snap.data())),
            (error) => {
                console.error('Error in settings subscription:', error);
                onError?.(error);
            },
        );
    }

    /**
     * Add an address to the beta whitelist. Normalized to lowercase so it matches
     * however the user capitalises it at sign-in; arrayUnion keeps it idempotent.
     */
    static async addWhitelistedEmail(email: string): Promise<void> {
        await updateDoc(doc(firestore, this.COLLECTION_NAME, this.SETTINGS_DOC_ID), {
            [FieldPaths.WHITELISTED_EMAILS]: arrayUnion(normalizeEmail(email)),
        });
    }

    /**
     * Remove an address from the beta whitelist. Takes the stored value verbatim
     * (arrayRemove matches exactly), so pass what the list rendered.
     */
    static async removeWhitelistedEmail(email: string): Promise<void> {
        await updateDoc(doc(firestore, this.COLLECTION_NAME, this.SETTINGS_DOC_ID), {
            [FieldPaths.WHITELISTED_EMAILS]: arrayRemove(email),
        });
    }

    /**
     * Turn the whitelist gate on or off. Off lets every verified account through,
     * leaving the list intact for a later closed period.
     */
    static async setWhitelistEnabled(enabled: boolean): Promise<void> {
        await updateDoc(doc(firestore, this.COLLECTION_NAME, this.SETTINGS_DOC_ID), {
            [FieldPaths.WHITELIST_ENABLED]: enabled,
        });
    }

    /**
     * Monthly all-access subscription price (settings/settings.subscriptionPrice).
     */
    static async getSubscriptionPrice(): Promise<string | null> {
        const snap = await getDoc(doc(firestore, this.COLLECTION_NAME, this.SETTINGS_DOC_ID));
        if (!snap.exists()) return null;
        return this.fromFirestore(snap.data()).subscriptionPrice;
    }

    /**
     * The current plugin release version (settings/versions.currentVersion), used
     * to build the installer download links below. Other fields on that document
     * are per-version release timestamps, written by packaging/deploy_release.sh.
     */
    static async getCurrentVersion(): Promise<string | null> {
        const snap = await getDoc(doc(firestore, this.COLLECTION_NAME, this.VERSIONS_DOC_ID));
        if (!snap.exists()) return null;
        return this.versionsFromFirestore(snap.data()).currentVersion;
    }

    /**
     * Subscribe to settings/versions (current version + per-version release
     * dates, written by packaging/deploy_release.sh). Returns an Unsubscribe.
     */
    static subscribeVersions(
        onUpdate: (versions: DatabaseVersions) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        const versionsRef = doc(firestore, this.COLLECTION_NAME, this.VERSIONS_DOC_ID);
        return onSnapshot(
            versionsRef,
            (snap) => onUpdate(this.versionsFromFirestore(snap.data())),
            (error) => {
                console.error('Error in versions subscription:', error);
                onError?.(error);
            },
        );
    }

    /**
     * Binaries are uploaded with public-read access by packaging/deploy_release.sh
     * to binaries/<version>/<filename> in Cloud Storage, so the URL can be built
     * directly without a Storage SDK round-trip.
     */
    private static binaryDownloadUrl(version: string, filename: string): string {
        return `https://storage.googleapis.com/${storageBucketName}/binaries/${encodeURIComponent(version)}/${encodeURIComponent(filename)}`;
    }

    static getMacDownloadUrl(version: string): string {
        return this.binaryDownloadUrl(version, `${PRODUCT_NAME}-${version}-macOS.pkg`);
    }

    static getWindowsDownloadUrl(version: string): string {
        return this.binaryDownloadUrl(version, `${PRODUCT_NAME}-${version}-Windows.exe`);
    }
}
